<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import type { FileInputProps } from '../types';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { mdiPaperclip } from '@mdi/js';

defineOptions({
  inheritAttrs: true,
});

const fileInput = ref<HTMLInputElement>();

const hasFocus = ref(false);

const props = withDefaults(defineProps<FileInputProps>(), { mode: 'edit' });

const passdownProps = computed(() => ({
  ...props,
  label: undefined,
  readonly: props.mode === 'details' || props.readonly,
}));

const errorMessageDisplay = computed(() => (hasFocus.value ? undefined : props.errorMessage));

const attrs = useAttrs();

const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const displayPlaceholder = computed(() => {
  return props.modelValue ? 'none' : 'block';
});

const emits = defineEmits(['update:modelValue']);

const slots = defineSlots<{ [key: string]: unknown }>();

function onUpdate(files: File | File[]) {
  emits('update:modelValue', files);
}
</script>

<template>
  <div :class="$attrs.class">
    <FormLabel :infoMessage="infoMessage" :label="label" :disabled="props.disabled">
      <template #default="{ onBlur, onFocus, hoverProps }">
        <v-file-input
          ref="fileInput"
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps }"
          :modelValue="modelValue"
          :error="Boolean(errorMessageDisplay)"
          :hint="errorMessageDisplay || hint"
          :persistentHint="Boolean(errorMessageDisplay) || persistentHint"
          :readonly="mode === 'details'"
          prependIcon=""
          @update:modelValue="onUpdate"
          @focus="
            hasFocus = true;
            onFocus();
          "
          @blur="
            hasFocus = false;
            onBlur();
          "
        >
          <template v-for="(_, name) in slots" #[name]="slotData">
            <slot :name="name" v-bind="slotData as any" />
          </template>
          <template #append>
            <button @click.prevent="fileInput?.click()">
              <v-icon :icon="mdiPaperclip" color="secondary" />
            </button>
          </template>
          <template #label>{{ $attrs.placeholder }}</template>
        </v-file-input>
      </template>
    </FormLabel>
  </div>
</template>
<style lang="scss" scoped>
@use '@/styles/mixins';

:deep(.v-input--horizontal .v-input__append) {
  margin-inline-start: 10px;
}
:deep(.v-label.v-field-label.v-label-overwrite) {
  display: v-bind(displayPlaceholder);
  opacity: 0.4;
  @include mixins.typography('body-1');
  color: rgb(var(--v-theme-on-surface));
  font-weight: 500;
  font-size: 16px;
  margin-inline-start: 10px;
  transition: none;
}

:deep(.v-field) {
  &.v-field--active {
    .v-label.v-field-label.v-label-overwrite {
      display: none;
    }
  }
  .v-label.v-field-label--floating {
    display: none !important;
    transition: none;
  }
  &.v-field--variant-outlined {
    .v-field__outline__notch::before {
      opacity: var(--v-field-border-opacity);
    }
  }
}
</style>
